import { creativeSaveErrorKey } from "@/lib/api/creative-library-errors";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "@digital-twins/design-system/icons";
import type { CreativeMaterialRole } from "@digital-twins/shared-types";
import { CreativeEditorDialog } from "./CreativeEditorDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreativeFilingFields } from "@/components/creative-materials/CreativeFilingFields";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { CreativeFilingEditorProps } from "@/types/creative-library";
export function CreativeFilingEditor({
  open,
  item,
  brands,
  products,
  onClose,
  onSaved,
}: CreativeFilingEditorProps) {
  const { t } = useTranslation("app");
  const [title, setTitle] = useState("");
  const [role, setRole] = useState<CreativeMaterialRole>("composition");
  const [brandToken, setBrandToken] = useState("none");
  const [productToken, setProductToken] = useState("none");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ReturnType<
    typeof creativeSaveErrorKey
  > | null>(null);
  useEffect(() => {
    if (!open || !item) return;
    setTitle(item.title);
    setRole(item.role);
    setBrandToken(item.brandToken ?? "none");
    setProductToken(item.productToken ?? "none");
    setError(null);
  }, [open, item]);
  async function save() {
    if (!item?.canEdit) return;
    setSaving(true);
    setError(null);
    try {
      await creativeLibraryApi.updateCreative(item.token, {
        revision: item.revision,
        title: title.trim(),
        role,
        brandToken: brandToken === "none" ? null : brandToken,
        productToken: productToken === "none" ? null : productToken,
      });
      onSaved();
      onClose();
    } catch (error) {
      setError(creativeSaveErrorKey(error));
    } finally {
      setSaving(false);
    }
  }
  return (
    <CreativeEditorDialog
      open={open}
      busy={saving}
      onClose={onClose}
      kind="creatives"
      title={t("creativeLibrary.editFiling")}
      description={t("creativeLibrary.filingHint")}
      testId="creative-filing-editor"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={saving} onClick={onClose}>
            {t("creativeLibrary.cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={saving || !title.trim()}
            onClick={() => void save()}
            data-testid="creative-filing-save"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("save", { ns: "common" })}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="creative-filing-title">
            {t("creativeLibrary.title")}
          </Label>
          <Input
            id="creative-filing-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            data-testid="creative-filing-title"
            disabled={saving}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>{t("creativeLibrary.referenceRole")}</Label>
          <Select
            value={role}
            onValueChange={(value) => setRole(value as CreativeMaterialRole)}
            disabled={saving}
          >
            <SelectTrigger
              aria-label={t("creativeLibrary.referenceRole")}
              data-testid="creative-filing-role"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(
                ["product", "logo", "style", "composition", "identity"] as const
              ).map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`creativeLibrary.roles.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <CreativeFilingFields
          brands={brands}
          products={products}
          brandToken={brandToken}
          productToken={productToken}
          disabled={saving}
          testIdPrefix="creative-filing"
          onChange={(brand, product) => {
            setBrandToken(brand);
            setProductToken(product);
          }}
        />
        {error && (
          <Text size="sm" className="text-destructive" role="alert">
            {t(error)}
          </Text>
        )}
      </div>
    </CreativeEditorDialog>
  );
}
